/**
 * NetsuiteAIEditor
 *
 * Workflow node editor for the "NetSuite AI" action.
 *
 * Architecture (three required pieces + optional orchestration):
 *   ① AI Config     — which LLM + the system prompt (tells the AI what it can do)
 *   ② NetSuite Config — credentials to connect to the customer's NetSuite instance
 *   ③ MCP Servers   — the actual NetSuite API tools (search_orders, search_cases, etc.)
 *   ④ Specialists   — optional: other AI Configs this AI can delegate sub-topics to
 *
 * The NetSuite AI node is NOT the MCP server. The MCP server is what gives the AI
 * tools to call. This node orchestrates the conversation; the MCP server provides the data.
 *
 * This pattern is generic — Shopify AI, BigCommerce AI, etc. follow the same 4-step model.
 */
import * as React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import useAIConfigs from "@/hooks/useAIConfigs";
import useNetSuiteConfigs from "@/hooks/useNetSuiteConfigs";
import useMcpServers from "@/hooks/useMcpServers";
import AISetupGuide from "./AISetupGuide";
import AISpecialistSection from "./AISpecialistSection";
import AIJumpTargetSection from "./AIJumpTargetSection";
import AIEscalationSection from "./AIEscalationSection";
import AIContextInspector from "./AIContextInspector";
import McpStaleBadge from "./McpStaleBadge";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

// AI providers that support MCP tool calling
const MCP_SUPPORTED_PROVIDERS = ["claude", "openai", "azure-openai", "anthropic"];

// NetSuite-specific scenario prompts to copy into an AI config's system prompt.
// Each scenario gives the AI context on how to handle that topic.
// Tool names reference the standard NetSuite MCP tool naming convention.
const NETSUITE_SCENARIOS = [
  {
    id: "orders",
    label: "Order Status Lookup",
    description: "Customer wants to check order status, ship date, or tracking number.",
    tools: ["search_orders"],
    prompt: `When the customer asks about an order, ask for their order number or the email address on the account if not already provided. Use the search_orders tool to find the order. Return the order status, estimated ship date, and tracking number if available. If the order is not found, apologize and offer to connect them with a support agent. For multiple orders, list the 3 most recent ones.`
  },
  {
    id: "cases",
    label: "Support Case Status",
    description: "Customer wants the status of a support or service case.",
    tools: ["search_cases"],
    prompt: `When the customer asks about a support case or ticket, ask for their case number or the email associated with their account. Use the search_cases tool to retrieve the case status, priority, and date of last activity. If the case is escalated or past SLA, acknowledge this and offer to escalate to a human agent. Never promise specific resolution times unless you can confirm them from the record.`
  },
  {
    id: "customers",
    label: "Customer Account Lookup",
    description: "Look up customer account details, contact info, or account history.",
    tools: ["find_customer"],
    prompt: `When the customer asks about their account, use the find_customer tool to look up their record by email or account number. You can share non-sensitive account details like company name, assigned rep, and account status. Never share payment information, credit limits, or other sensitive financial data without explicit authorization from an authenticated manager or admin.`
  },
  {
    id: "leads",
    label: "Lead Qualification",
    description: "Collect visitor details and create a qualified lead, or check if they already exist.",
    tools: ["search_leads", "create_lead"],
    prompt: `When a visitor appears to be a new prospect, first use the search_leads tool with their email to check if they already exist. If not found, collect: company name, contact name, email, phone, and their main need or interest. Then call create_lead to record them. Confirm the lead was created and tell the visitor your sales team will follow up within 1 business day. If they already exist as a lead, acknowledge that and offer to pass a message to their assigned rep.`
  },
  {
    id: "inventory",
    label: "Inventory / Product Lookup",
    description: "Check product availability, pricing, or item details.",
    tools: ["search_inventory"],
    prompt: `When the customer asks about product availability or item details, use the search_inventory tool with the item name or SKU. Return the current stock level, warehouse location, and expected restock date if out of stock. If multiple variants or sizes exist, list them clearly. Never quote pricing from memory — always fetch it from the record. If an item is discontinued, say so and suggest the nearest alternative.`
  },
  {
    id: "invoice",
    label: "Invoice / Billing Lookup",
    description: "Customer wants to check invoice status or an outstanding balance.",
    tools: ["search_invoices"],
    prompt: `When the customer asks about an invoice or bill, ask for their invoice number or account email. Use the search_invoices tool to find the record. Share the invoice total, due date, and payment status. If the invoice is overdue, acknowledge this professionally and offer to connect them with the billing team. Never share payment card numbers, bank details, or other customers' billing information.`
  },
  {
    id: "multi_specialist",
    label: "Full NetSuite Specialist (All Topics)",
    description: "Combined prompt for a NetSuite specialist that handles orders, cases, inventory, and billing. Used as a specialist agent from a main general AI.",
    tools: ["search_orders", "search_cases", "find_customer", "search_inventory"],
    prompt: `You are a NetSuite specialist. The main AI has routed this conversation to you because it involves ERP data. You have access to the following tools:\n\n- search_orders: find orders by number or email\n- search_cases: find support cases by case number or email\n- find_customer: look up a customer account\n- search_inventory: check product availability\n- search_leads: find prospects and leads\n\nAlways ask for the customer's email or order/case number before searching. When you have resolved the request or if the issue requires actions beyond your tools, call return_to_previous to hand control back to the main AI. Never fabricate data — if a record is not found, say so and offer to escalate.`
  }
];

export default function NetsuiteAIEditor({ node, onUpdate }: Props) {
  const { aiConfigurations } = useAIConfigs();
  const { items: netSuiteConfigs, loading: nsLoading } = useNetSuiteConfigs(0);
  const { items: mcpServers } = useMcpServers();
  const [copiedScenario, setCopiedScenario] = useState<string | null>(null);
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);

  const aiOptions = aiConfigurations.map((ai) => ({ value: ai.id, label: ai.name }));
  const nsOptions = netSuiteConfigs.map((ns) => ({
    value: ns.id,
    label: ns.displayName || ns.company || `Config #${ns.id}`
  }));

  const selectedAiConfig = aiConfigurations.find((ai) => ai.id === node.aiConfigId);
  const enabledMcpServers = mcpServers.filter((s) => s.enabled);

  const providerName =
    (selectedAiConfig as { provider?: string } | undefined)?.provider?.toLowerCase() ?? "";
  const providerUnsupported =
    enabledMcpServers.length > 0 &&
    selectedAiConfig != null &&
    providerName !== "" &&
    !MCP_SUPPORTED_PROVIDERS.some((p) => providerName.includes(p));

  const selectedMcpIds: number[] = Array.isArray(node.mcpServerIds) ? node.mcpServerIds : [];

  const enabledToolNames = enabledMcpServers
    .filter((s) => selectedMcpIds.includes(s.id))
    .flatMap((s) => s.toolNames ?? []);

  function toggleMcpServer(id: number, checked: boolean) {
    const next = checked ? [...selectedMcpIds, id] : selectedMcpIds.filter((x) => x !== id);
    onUpdate({ mcpServerIds: next.length > 0 ? next : undefined });
  }

  async function copyScenarioPrompt(id: string, prompt: string) {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedScenario(id);
      setTimeout(() => setCopiedScenario(null), 2000);
    } catch {
      // fallback: select text if clipboard API unavailable
    }
  }

  return (
    <>
      {/* ── How it works banner ── */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 mb-3">
        <p className="text-xs font-semibold text-blue-800 mb-1">How this node works</p>
        <p className="text-xs text-blue-700 leading-relaxed">
          This AI node connects to your NetSuite instance via an MCP tool server.
          Use it as a specialist — have your main workflow AI delegate to it when
          order, case, or inventory topics come up.
        </p>
        <p className="text-xs text-blue-600 mt-1.5">
          Recommended: <strong>Main AI → detects NetSuite question → delegates to NetSuite specialist → returns to main AI</strong>
        </p>
      </div>

      {/* ── Step 1: AI Config ── */}
      <FormGroup label="AI Config">
        {aiConfigurations.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No AI configs found.{" "}
            <Link to="/bots" className="underline font-medium">
              Create an AI Config first →
            </Link>
            <p className="mt-1 text-slate-500">
              The AI Config holds the system prompt — it tells the AI what NetSuite operations it
              can perform.
            </p>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select AI Config —" }, ...aiOptions]}
            value={node.aiConfigId ?? 0}
            onChange={(value) => onUpdate({ aiConfigId: Number(value) || undefined })}
          />
        )}
        {selectedAiConfig && !selectedAiConfig.prompt && (
          <p className="text-xs text-amber-600 mt-1">
            This AI config has no system prompt.{" "}
            <Link to="/bots" className="underline">
              Add a prompt →
            </Link>{" "}
            telling it what it can do in NetSuite.
          </p>
        )}
      </FormGroup>

      {/* ── Step 2: NetSuite Config ── */}
      <FormGroup label="NetSuite Config">
        {!nsLoading && netSuiteConfigs.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No NetSuite integration found.{" "}
            <Link to="/integrations/netsuite" className="underline font-medium">
              Add a NetSuite integration →
            </Link>
            <p className="mt-1 text-slate-500">
              The NetSuite config holds the credentials (OAuth tokens) needed to query your
              NetSuite instance.
            </p>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select NetSuite Config —" }, ...nsOptions]}
            value={node.netSuiteConfigId ?? 0}
            onChange={(value) => onUpdate({ netSuiteConfigId: Number(value) || undefined })}
          />
        )}
      </FormGroup>

      {/* ── Step 3: MCP Tool Servers ── */}
      <FormGroup label="MCP Tool Servers">
        {enabledMcpServers.length === 0 ? (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
            No MCP tool servers configured.{" "}
            <Link to="/integrations/mcp" className="text-blue-600 underline">
              Add an MCP server →
            </Link>
            <p className="mt-1">
              Without tools, the AI can only chat — it can't actually search NetSuite data. You'll
              need a NetSuite MCP server that exposes tools like{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">search_orders</code>,{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">search_cases</code>,
              etc.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 mt-1">
            {providerUnsupported && (
              <p className="text-amber-700 bg-amber-50 text-xs px-2 py-1 rounded border border-amber-200">
                MCP tools require Claude or OpenAI. The selected AI config may not support them.
              </p>
            )}
            {enabledMcpServers.map((server) => {
              const checked = selectedMcpIds.includes(server.id);
              return (
                <label
                  key={server.id}
                  className={`flex gap-2.5 cursor-pointer rounded border p-2.5 transition-colors ${
                    checked
                      ? "border-blue-300 bg-blue-50"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 shrink-0"
                    checked={checked}
                    onChange={(e) => toggleMcpServer(server.id, e.target.checked)}
                    aria-label={`Enable MCP server: ${server.name}`}
                  />
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{server.name}</span>
                      {server.toolCount > 0 && (
                        <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                          {server.toolCount} {server.toolCount === 1 ? "tool" : "tools"}
                        </span>
                      )}
                      <McpStaleBadge lastDiscoveredAt={server.lastDiscoveredAt} />
                    </div>
                    {server.description && (
                      <span className="text-xs text-slate-500">{server.description}</span>
                    )}
                    {server.toolNames.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {server.toolNames.slice(0, 6).map((t) => (
                          <span
                            key={t}
                            className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono"
                          >
                            {t}
                          </span>
                        ))}
                        {server.toolNames.length > 6 && (
                          <span className="text-xs text-slate-400 px-1 py-0.5">
                            +{server.toolNames.length - 6} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </FormGroup>

      {/* ── Scenario Prompt Helper ── */}
      <div className="mt-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
            Scenario Prompts
          </span>
          <span className="text-xs text-slate-400 italic">for your AI Config</span>
        </div>
        <p className="text-xs text-slate-500 mb-2 leading-relaxed">
          Copy these snippets into your AI Config's system prompt to tell the AI how to handle
          specific NetSuite scenarios.
        </p>
        <div className="flex flex-col gap-2">
          {NETSUITE_SCENARIOS.map((scenario) => {
            const isExpanded = expandedScenario === scenario.id;
            const isCopied = copiedScenario === scenario.id;
            return (
              <div
                key={scenario.id}
                className="border border-slate-200 rounded bg-white overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setExpandedScenario(isExpanded ? null : scenario.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                  aria-expanded={isExpanded}
                >
                  <div>
                    <span className="text-xs font-medium text-slate-800">{scenario.label}</span>
                    {!isExpanded && (
                      <p className="text-xs text-slate-400 mt-0.5">{scenario.description}</p>
                    )}
                  </div>
                  <span className="text-slate-400 text-xs ml-2 shrink-0">
                    {isExpanded ? "▲" : "▼"}
                  </span>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3">
                    <p className="text-xs text-slate-500 mb-1.5">{scenario.description}</p>
                    {scenario.tools && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {scenario.tools.map(t => (
                          <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{t}</span>
                        ))}
                      </div>
                    )}
                    <pre className="text-xs bg-slate-900 text-slate-100 rounded p-2.5 whitespace-pre-wrap leading-relaxed font-mono mb-2">
                      {scenario.prompt}
                    </pre>
                    <button
                      type="button"
                      onClick={() => copyScenarioPrompt(scenario.id, scenario.prompt)}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                        isCopied
                          ? "border-green-300 bg-green-50 text-green-700"
                          : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      {isCopied ? "✓ Copied!" : "Copy prompt snippet"}
                    </button>
                    <p className="text-xs text-slate-400 mt-1.5">
                      Paste into your AI Config's system prompt, then customize for your business.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Context Inspector (debug) ── */}
      <AIContextInspector mcpToolNames={enabledToolNames} />

      {/* ── Step 4: Specialist Agents (orchestration) ── */}
      <div className="mt-4 pt-3 border-t border-slate-200">
        <AISpecialistSection
          node={node}
          onUpdate={onUpdate}
          aiConfigurations={aiConfigurations}
          enabledMcpServers={enabledMcpServers}
        />
      </div>

      {/* ── Escalation & Handoff ── */}
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIEscalationSection node={node} onUpdate={onUpdate} />
      </div>

      {/* ── Jump to Workflow Section (advanced / custom) ── */}
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIJumpTargetSection node={node} onUpdate={onUpdate} />
      </div>
    </>
  );
}
